<template>
  <div :class="$style.wrapper">
    <div :class="$style.subNavWrapper">
      <b-row
        v-if="!isFriendLoading || friend"
        class="p-3"
      >
        <font-awesome-icon
          icon="user"
          size="2x"
        />
        <div class="pl-3 text-center">
          <BaseTextH2 class="m-0">
            {{ friend.name }}
          </BaseTextH2>
        </div>
      </b-row>
      <b-row
        v-else
        class="p-3"
      >
        <BaseLoading />
      </b-row>
    </div>
    <div :class="$style.content">
      <BaseLoading
        v-if="isPicksLoading && !picks"
        full-screen
      />
      <transition-group
        tag="div"
        enter-active-class="fadeInDown"
        leave-active-class="fadeOutDown"
        :duration="{ enter: 1000, leave: 400 }"
        mode="out-in"
      >
        <template
          v-if="!isPicksLoading || picks"
        >
          <div
            v-for="(pick, index) in picks"
            :key="`pick-${pick.id}`"
            :class="$style.roundWrapper"
            :style="{ animationDelay: `${index * 0.05}s` }"
          >
            <div :class="$style.headerWrapper">
              <b-container class="d-flex flex-column flex-sm-row justify-content-between align-items-center px-3">
                <b-link
                  :to="{ name: 'round-detail', params: { roundId: pick.round.id } }"
                  :class="$style.cursorPointer"
                  router-tag="span"
                >
                  <TheRoundTitle
                    :image="pick.round.season.league.image_url"
                    :league-name="pick.round.season.league.name[$i18n.locale]"
                    :round-name="pick.round.name[$i18n.locale]"
                  />
                </b-link>
                <b-link
                  :to="{ name: 'round-detail', params: { roundId: pick.round.id } }"
                  :class="$style.cursorPointer"
                  router-tag="span"
                >
                  <span class="py-1">
                    {{ pick.round.start | formatDateTime }}
                  </span>
                </b-link>
              </b-container>
            </div>
            <div
              v-if="pick.is_win"
              :class="$style.subHeaderWrapper"
            >
              <b-container>
                <b-img
                  :src="require(`@/assets/images/icons/trophy.png`)"
                  :class="$style.trophyImage"
                />
              </b-container>
            </div>
            <!-- summer games -->
            <div
              v-if="pick.round.season.league.type==1"
              :class="$style.matchesWrapper"
            >
              <b-container>
                <div
                  v-for="score in pick.scores"
                  :key="`score-${score.id}`"
                >
                  <TheSummerScoreOverview
                    :score="score"
                    :athletes="pick.athletes"
                  />
                </div>
              </b-container>
            </div>
            <!-- regular games -->
            <b-container
              v-else
              :class="$style.athletesWrapper"
            >
              <div
                v-for="i in 8"
                :key="`athlete-${i}`"
                :class="$style.athleteWrapper"
              >
                <BaseAthleteScoreCircle
                  v-if="pick.athletes[i]"
                  :id="pick.athletes[i].athlete.id"
                  :name="pick.athletes[i].athlete.last_name"
                  :score="pick.athletes[i].athlete.round_score"
                  :avatar="pick.athletes[i].athlete.image"
                  :showdetail="true"
                />
                <BaseAthleteScoreCircle v-else />
              </div>
            </b-container>
          </div>
        </template>
      </transition-group>
      <BaseAlert
        v-if="!isPicksLoading && !picks.length"
        is-center
        variant="light"
      >
        {{ $t('user-has-not-picks-yet') }}
      </BaseAlert>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns'
import { mapActions, mapState } from 'vuex'
import BaseAlert from '@/components/Atoms/BaseAlert'
import BaseTextH2 from '@/components/Atoms/Typography/BaseTextH2'
import BaseLoading from '@/components/Atoms/BaseLoading'
import BaseAthleteScoreCircle from '@/components/Atoms/BaseAthleteScoreCircle'
import TheRoundTitle from '@/components/Molecules/TheRoundTitle'
import TheSummerScoreOverview from '@/pages/Clan/Components/Molecules/TheSummerScoreOverview'

export default {
  name: 'ClanFriendPicks',
  components: {
    BaseAlert,
    BaseTextH2,
    BaseLoading,
    BaseAthleteScoreCircle,
    TheRoundTitle,
    TheSummerScoreOverview,
  },
  metaInfo() {
    return {
      title: this.$t('page-titles.picks'),
      meta: [
        { name: 'description', content: this.$t('page-descriptions.picks') },
      ],
    }
  },
  data() {
    return {
      userId: this.$route.params.userId,
    }
  },
  computed: {
    ...mapState({
      isPicksLoading: state => state['profile/clanMemberPicks'].isPicksLoading,
      isFriendLoading: state => state['profile/clanFriends'].isFriendLoading,
    }),
    picks() {
      return this.$store.getters['profile/clanMemberPicks/getFriendPicks']({ friendId: this.userId })
    },
    friend() {
      return this.$store.getters['profile/clanFriends/getFriendById']({ friendId: this.userId })
    },
  },
  created() {
    this.fetchPicks({ userId: this.userId })
    this.fetchFriend({ userId: this.userId })
  },
  methods: {
    ...mapActions({
      fetchPicks: 'profile/clanMemberPicks/fetchPicks',
      fetchFriend: 'profile/clanFriends/fetchFriend',
    }),
    formatDate(dateString) {
      return format(dateString, 'DD/MM/YY HH:mm')
    },
  },
}
</script>

<style lang="scss" module>
  @import '../../../../assets/scss/variables';
  .wrapper {
    width: 100%;
    .subNavWrapper {
      border-bottom: 2px solid #f4f3f3;
      display: flex;
      justify-content: center;
      padding: 5px;
    }
    .roundWrapper {
      border-bottom: 2px solid $color-bleached-silk;
    }
    .headerWrapper{
      display: flex;
      justify-content: center;
      background-color: $color-bleached-silk;
      margin-left: -15px;
      margin-right: -15px;
    }
    .subHeaderWrapper{
      display: flex;
      justify-content: center;
      background-color: #130035;
      margin-left: -15px;
      margin-right: -15px;
      padding: 10px;
    }
    .content {
      width: 100%;
      overflow-x: hidden;
    }
  }

  .athletesWrapper,
  .matchesWrapper {
    display: flex;
    justify-content: space-around;
    align-items: center;
    flex-wrap: wrap;
  }
  .athleteWrapper {
    padding: 1em;
  }
  .trophyImage{
    max-height: 40px;
    padding-left: 10px;
  }
  .cursorPointer {
    cursor: pointer;
  }
</style>
